import { LoginCallback, useOktaAuth } from '@okta/okta-react';
import type { WidgetOptions } from '@okta/okta-signin-widget';
import { useContext, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AuthLayout, OktaSignInWidget } from '~/modules/auth/components';
import { LoginError } from '~/modules/auth/components/LoginError';
import { getOktaConfig } from '~/modules/auth/lib/okta-config';
import { BrandContext } from '~/modules/branding';
import { LoadingScreen } from '~/modules/navigation/components/LoadingScreen';

function getConfigOverrides() {
  const loginUrl = new URLSearchParams(window.location.search);
  const recovery = loginUrl.get('recovery') ?? undefined;
  const activate = loginUrl.get('activate') ?? undefined;

  const config: Partial<WidgetOptions> = {
    recoveryToken: recovery ?? activate,
  };

  if (activate) {
    config.i18n = {
      en: {
        'password.reset.title.generic': 'Set your password',
        'password.reset': 'Create My Account',
        'factor.push.description': 'Set up Okta Verify to continue.',
        'enroll.choices.submit.configure': 'Continue',
      },
    };
  }

  return config;
}

/**
 * Renders Okta's LoginCallback only for a real Okta redirect.
 *
 * LoginCallback would spin forever when /login/callback is opened directly
 * (no code/state/error in the URL), so in that case send the user to "/",
 * where SecureRoute redirects unauthenticated users to sign-in.
 *
 * The decision is made once on mount: Okta strips the OAuth params from the
 * URL (history.replaceState) while it processes the redirect, so re-evaluating
 * isLoginRedirect() on a later render would wrongly treat a real redirect as a
 * direct visit and navigate away mid-login.
 */
function CallbackRoute() {
  const { oktaAuth } = useOktaAuth();
  const [isLoginRedirect] = useState(() => oktaAuth.isLoginRedirect());

  if (!isLoginRedirect) {
    return <Navigate to="/" replace />;
  }

  return (
    <div data-testid="login-callback" className="flex min-h-dvh min-w-full">
      <LoginCallback
        errorComponent={LoginError}
        loadingElement={<LoadingScreen />}
      />
    </div>
  );
}

CallbackRoute.displayName = 'CallbackRoute';

/**
 * Okta router called on everything /login/*
 */
function OktaAuthRouter() {
  const brand = useContext(BrandContext);
  const config = getOktaConfig(brand, getConfigOverrides());
  const { oktaAuth, authState } = useOktaAuth();

  const onSuccess = () => {
    oktaAuth.handleRedirect();
  };

  const onError = (...args: unknown[]) => {
    console.error('Sign in error:', args);
  };

  if (authState?.isAuthenticated) {
    return <Navigate to="/" />;
  }

  return (
    <Routes data-testid="auth-router">
      <Route path="/callback" element={<CallbackRoute />} />
      <Route
        key="Home"
        path="/welcome"
        element={
          <AuthLayout>
            <OktaSignInWidget
              config={config}
              onError={onError}
              onSuccess={onSuccess}
            />
          </AuthLayout>
        }
      />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

OktaAuthRouter.displayName = 'OktaAuthRouter';

export { OktaAuthRouter };
